import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { VERIFY_OPERATOR_PIN, SET_OPERATOR_PIN } from 'lib/api/mutations';
import { getPinErrorMessage } from 'lib/utils/pinErrors';

/**
 * Hook for PIN verification and PIN reset
 * @param {Object} options
 * @param {string} options.operatorId - The operator ID for PIN verification
 * @returns {Object} PIN verification/set functions and state
 */
export const usePinVerification = ({ operatorId }) => {
  const [error, setError] = useState(null);
  const [lockoutDelay, setLockoutDelay] = useState(null);

  const [
    verifyPinMutation,
    { loading: isVerifying },
  ] = useMutation(VERIFY_OPERATOR_PIN, { fetchPolicy: 'no-cache' });

  const [
    setPinMutation,
    { loading: isSettingPin },
  ] = useMutation(SET_OPERATOR_PIN, { fetchPolicy: 'no-cache' });

  const isLoading = isVerifying || isSettingPin;

  /**
   * Verify an operator's PIN
   * @param {string} pin - The PIN to verify
   * @returns {Promise<Object>} Verification result including isInitialPin and pinExpiration
   */
  const verifyPin = useCallback(
    async (pin) => {
      if (!operatorId) {
        throw new Error('Operator ID is required');
      }

      setError(null);
      setLockoutDelay(null);

      try {
        const { data } = await verifyPinMutation({
          variables: {
            input: {
              operatorId: String(operatorId),
              pin,
            },
          },
        });

        const result = data?.verifyPinResponse;

        if (result?.code === 'ValidationError' && result?.errors) {
          const pinError = result.errors.find((err) => {
            return err.field === 'pin';
          });
          if (pinError && pinError.message) {
            setError(pinError.message);
            throw new Error(pinError.message);
          }
          setError(result.message || 'Validation failed');
          throw new Error(result.message || 'Validation failed');
        }

        if (result?.valid === null && result?.error) {
          setError(result.error);
          throw new Error(result.error);
        }

        if (result?.delay) {
          setLockoutDelay(result.delay);
        }

        return {
          valid: result?.valid,
          error: result?.error,
          delay: result?.delay,
          attempts: result?.attempts,
          isInitialPin: result?.isInitialPin || false,
          pinExpiration: result?.pinExpiration || null,
        };
      } catch (err) {
        const message = getPinErrorMessage(
          err,
          'Verification failed. Please try again.'
        );
        setError(message);
        throw new Error(message);
      }
    },
    [operatorId, verifyPinMutation]
  );

  /**
   * Set a new PIN for the operator (used during PIN reset flow)
   * @param {string} currentPin - The current/verified PIN
   * @param {string} newPin - The new PIN to set
   * @returns {Promise<Object>} Set PIN result
   */
  const setPin = useCallback(
    async (currentPin, newPin) => {
      if (!operatorId) {
        throw new Error('Operator ID is required');
      }

      setError(null);

      try {
        const { data } = await setPinMutation({
          variables: {
            input: {
              operatorId: String(operatorId),
              currentPin,
              pin: newPin,
            },
          },
        });

        const result = data?.setPinResponse;

        if (result?.code === 'ValidationError' && result?.errors) {
          const pinError = result.errors.find((err) => {
            return err.field === 'pin';
          });
          if (pinError && pinError.message) {
            setError(pinError.message);
            throw new Error(pinError.message);
          }
          setError(result.message || 'Validation failed');
          throw new Error(result.message || 'Validation failed');
        }

        if (result?.error) {
          setError(result.error);
          throw new Error(result.error);
        }

        return result;
      } catch (err) {
        const message = getPinErrorMessage(
          err,
          'Failed to set PIN. Please try again.'
        );
        setError(message);
        throw new Error(message);
      }
    },
    [operatorId, setPinMutation]
  );

  return {
    verifyPin,
    setPin,
    isLoading,
    error,
    lockoutDelay,
  };
};

export default usePinVerification;
